<template>
  <div class="search">
    <div class="search__tabs">
      <button
        class="search__tab"
        :class="{ 'search__tab--active': activeAssetTab === 'coin' }"
        @click="setSearchType('coin')"
      >Крипта</button>
      <button
        class="search__tab"
        :class="{ 'search__tab--active': activeAssetTab === 'stock_ru' }"
        @click="setSearchType('stock_ru')"
      >RU-акции</button>
    </div>
    <div class="search__wrapper" @click="focusInput" :class="isFocus ? 'search__wrapper--focus' : ''">
      <input class="search__input" v-model="inputValue" ref="inputRef" @focus="isFocus = true" @blur="isFocus = false" />
      <button class="search__cross-btn" v-if="inputValue" @click="clearInput">
        <CrossIcon />
      </button>
    </div>
    <div class="search__ldr-block" v-if="isLoading">
      <LoaderCircle :color="'var(--circle-loader)'" :size="32" />
    </div>
    <div class="search__empty-block" v-else-if="inputValue.trim().length > 1 && !searchResults.length && !isLoading">
      <p class="search__empty-txt">По вашему запросу ничего не найдено</p>
    </div>
    <div class="search__scroll-box" :class="{ 'search__scroll-box--top': !isAtTop, 'search__scroll-box--bottom': !isAtBottom }" v-else>
      <ul class="search__output-list" ref="scrollContainer" @scroll="handleScroll">
        <li
          class="search__output-item"
          v-for="item in searchResults"
          :key="itemId(item)"
          @click="(!isChecked(item)) && toggleCheck(item)"
        >
          <AvatarImage class="search__coin-avatar" :src="item.image" :size="24" />
          <p class="search__output-text">{{ item.name }}</p>
          <CheckboxInput class="search__checkbox" :modelValue="isChecked(item) || isPending(item)" :disabled="isChecked(item)" />
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
  import { useStore } from 'vuex';
  import { ref, watch, onMounted, nextTick } from 'vue';
  import { debounce } from 'lodash';
  import { searchBriefcaseCoins } from '@/Groups/Briefcase/private/api/searchBriefcaseCoins';
  import { searchRuStocksBriefcase } from '@/Groups/Briefcase/private/api/searchRuStocksBriefcase';
  import { useBriefcaseState } from '@/Groups/Briefcase/private/composables/computedValues';
  //-------------------------------
  import CrossIcon from '@/Groups/Briefcase/private/svg/CrossIcon.vue';
  import { AvatarImage, CheckboxInput, LoaderCircle } from '@/Groups/Briefcase/private/imports/externalTemplates';
  //-------------------------------
  const store = useStore();
  //-------------------------------
  onMounted(() => {
    inputRef.value.focus();
  })
  //-------------------------------
  const { coinAssets, stockRuAssets, pendingAssets, activeAssetTab } = useBriefcaseState();
  //-------------------------------
  const inputValue = ref('');
  const inputRef = ref(null);
  const isLoading = ref(false);
  const searchResults = ref([]);
  const isFocus = ref(false);
  const scrollContainer = ref(null);
  const isAtTop = ref(true);
  const isAtBottom = ref(false);
  //Счётчик запросов поиска — растёт при каждом новом запросе/смене вкладки,
  //чтобы устаревший (пришедший после переключения) ответ не попал в searchResults
  let requestId = 0;
  //--------------------------------
  onMounted(async () => {
    await nextTick();
    if (scrollContainer.value) {
      handleScroll({ target: scrollContainer.value });
    }
  });
  //-------------------------------
  //Focus на поле
  const focusInput = () => {
    inputRef.value?.focus();
  };
  //Очистка поля
  const clearInput = () => {
    inputValue.value = '';
    searchResults.value = [];
  };
  //Идентификатор результата поиска вне зависимости от типа (api_id у монет, secid у RU-акций)
  const itemId = (item) => activeAssetTab.value === 'stock_ru' ? item.secid : item.api_id;
  //Переключение вкладки поиска (крипта/RU-акции) — общая со списком портфеля вкладка в сторе
  const setSearchType = (type) => {
    if (activeAssetTab.value === type) return;
    requestId++; // инвалидируем ещё не завершившийся запрос предыдущей вкладки
    store.dispatch('Briefcase/setActiveAssetTab', type);
    clearInput();
  };
  //Дебаунс-функция поиска
  const debouncedSearch = debounce(async (query) => {
    const trimmed = query.trim();
    const thisRequest = ++requestId;
    if (trimmed.length <= 1) {
      searchResults.value = [];
      return;
    }
    isLoading.value = true;
    try {
      const result = activeAssetTab.value === 'stock_ru'
        ? await searchRuStocksBriefcase(query)
        : await searchBriefcaseCoins(query);
      if (thisRequest !== requestId) return; // ответ устарел (сменилась вкладка/начался новый поиск) — отбрасываем
      searchResults.value = result.results;
    } catch (error) {
      console.error('Search error:', error);
      if (thisRequest !== requestId) return; // тот же случай устаревшего запроса — не трогаем актуальные результаты
      const message = error?.message || '';
      if (message.includes('Status: 500')) {
        searchResults.value = [];
      }
    } finally {
      if (thisRequest === requestId) {
        isLoading.value = false;
        await nextTick();
        handleScroll();
      }
    }
  }, 400);
  //Проверка, есть ли элемент в assets соответствующего типа
  const isChecked = (item) => {
    const assets = activeAssetTab.value === 'stock_ru' ? stockRuAssets.value : coinAssets.value;
    return assets.some(a =>
      (activeAssetTab.value === 'stock_ru' ? a.stock_ru?.secid : a.coin?.api_id) === itemId(item)
    );
  };
  //Для чекбокса — показывает, была ли выбрана монета/акция пользователем (временное добавление)
  const isPending = (item) => {
    return pendingAssets.value.some(a =>
      a.assetType === activeAssetTab.value && (activeAssetTab.value === 'stock_ru' ? a.secid : a.api_id) === itemId(item)
    );
  };
  //Переключатель чекбокса
  const toggleCheck = (item) => {
    store.dispatch('Briefcase/togglePendingAsset', { ...item, assetType: activeAssetTab.value });
  };
  //Обработчик скролла
  const handleScroll = (e) => {
    const target = e ? e.target : scrollContainer.value;
    if(!target) return;
    const {scrollTop, scrollHeight, clientHeight} = target;
    const tolerance = 1;
    isAtTop.value = scrollTop <= tolerance;
    isAtBottom.value = scrollHeight - (scrollTop + clientHeight) <= tolerance;
  }
  //-------------------------------
  watch(inputValue, (newVal) => {
    debouncedSearch(newVal);
  });
</script>

<style lang="scss" scoped>
  @import "./styles.scss";
</style>
